import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import { TextField, SelectField, ToggleField, InfoBox } from '../../../components/orizon-ui/FormField';
import { colataCrea, colataAggiorna } from '../../../lib/services/colataService';
import FormModal, { useFormSalva, useSporco } from '../../../components/colata/FormModal';
import SezioneConfig, { colonnaAttivo } from './SezioneConfig';
import { slugify, CHIAVE_VALIDA, vuoto, interoOrdine, COMPORTAMENTI, DECIMALI } from '../utils';

// ─── Hook comune: label + key (suggerita dalla label finché non viene toccata) ─────────────
const useLabelKey = (record) => {
  const [label, setLabelState] = useState(record?.label ?? '');
  const [key, setKeyState] = useState(record?.key ?? '');
  const [keyToccata, setKeyToccata] = useState(Boolean(record));

  const setLabel = (valore) => {
    setLabelState(valore);
    if (!record && !keyToccata) setKeyState(slugify(valore));
  };
  const setKey = (valore) => {
    setKeyToccata(true);
    setKeyState(valore);
  };
  return { label, key, setLabel, setKey };
};

const validaLabelKey = (label, key, modifica) => {
  const e = {};
  if (vuoto(label)) e.label = 'Obbligatorio';
  if (!modifica) {
    if (vuoto(key)) e.key = 'Obbligatorio';
    else if (!CHIAVE_VALIDA.test(key)) e.key = 'Solo minuscole, numeri e _';
  }
  return e;
};

// ─── Cause ───────────────────────────────────────────────────────────────────

const FormCausa = ({ record, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const lk = useLabelKey(record);
  const [ordine, setOrdine] = useState(String(record?.ordine ?? 0));
  const [attivo, setAttivo] = useState(record?.attivo ?? true);
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();
  const sporco = useSporco({ label: lk.label, key: lk.key, ordine, attivo });

  const invia = async () => {
    const nuovi = validaLabelKey(lk.label, lk.key, modifica);
    const n = interoOrdine(ordine);
    if (Number.isNaN(n)) nuovi.ordine = 'Numero intero';
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const payload = { label: lk.label.trim(), ordine: n, attivo, ...(modifica ? {} : { key: lk.key }) };
    await salva(
      () => (modifica ? colataAggiorna('cause', record.id, payload) : colataCrea('cause', payload)),
      modifica ? 'Causa aggiornata.' : 'Causa creata.',
      onSaved,
    );
  };

  return (
    <FormModal titolo={modifica ? 'Modifica causa' : 'Nuova causa'} etichetta="Codifiche · Cause" larghezza="md" sporco={sporco} salvando={salvando} onClose={onClose} onSubmit={invia}>
      <TextField label="Etichetta" name="label" value={lk.label} onChange={(v) => { lk.setLabel(v); pulisci('label'); }} obbligatorio errore={errori.label} autoFocus placeholder="es. Usura" />
      <TextField
        label="Chiave" name="key" value={lk.key} onChange={(v) => { lk.setKey(v); pulisci('key'); }}
        obbligatorio={!modifica} disabilitato={modifica} errore={errori.key} mono
        suggerimento={modifica ? 'La chiave non è modificabile.' : 'Identificativo interno: minuscole, numeri e _.'}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <TextField label="Ordine" name="ordine" type="number" value={ordine} onChange={(v) => { setOrdine(v); pulisci('ordine'); }} errore={errori.ordine} />
      </div>
      <ToggleField label="Attiva" sublabel={attivo ? 'Selezionabile' : 'Non selezionabile'} checked={attivo} onChange={setAttivo} />
    </FormModal>
  );
};

const COLONNE_CAUSE = [
  { key: 'ordine', label: 'Ordine', width: 90, align: 'center', render: (r) => <span className="text-[12px] font-bold tabular-nums text-muted-foreground">{r.ordine}</span> },
  { key: 'label', label: 'Etichetta', width: 260, render: (r) => <span className="text-[13px] font-black uppercase tracking-tight text-foreground/80">{r.label}</span> },
  { key: 'key', label: 'Chiave', width: 220, render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground">{r.key}</span> },
  colonnaAttivo,
];

// ─── Tipi di intervento ──────────────────────────────────────────────────────

const comportamentoDaRecord = (record) => {
  if (!record) return '';
  return record.tipo_movimento ?? 'nessuno';
};

const FormTipo = ({ record, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const lk = useLabelKey(record);
  const [comportamento, setComportamento] = useState(comportamentoDaRecord(record));
  const [ordine, setOrdine] = useState(String(record?.ordine ?? 0));
  const [attivo, setAttivo] = useState(record?.attivo ?? true);
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();
  const sporco = useSporco({ label: lk.label, key: lk.key, comportamento, ordine, attivo });

  const invia = async () => {
    const nuovi = validaLabelKey(lk.label, lk.key, modifica);
    if (vuoto(comportamento)) nuovi.tipo_movimento = 'Seleziona un comportamento';
    const n = interoOrdine(ordine);
    if (Number.isNaN(n)) nuovi.ordine = 'Numero intero';
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const payload = {
      label: lk.label.trim(),
      tipo_movimento: comportamento === 'nessuno' ? null : comportamento,
      ordine: n,
      attivo,
      ...(modifica ? {} : { key: lk.key }),
    };
    await salva(
      () => (modifica ? colataAggiorna('tipi', record.id, payload) : colataCrea('tipi', payload)),
      modifica ? 'Tipo di intervento aggiornato.' : 'Tipo di intervento creato.',
      onSaved,
    );
  };

  return (
    <FormModal titolo={modifica ? 'Modifica tipo di intervento' : 'Nuovo tipo di intervento'} etichetta="Codifiche · Tipi intervento" larghezza="md" sporco={sporco} salvando={salvando} onClose={onClose} onSubmit={invia}>
      <TextField label="Etichetta" name="label" value={lk.label} onChange={(v) => { lk.setLabel(v); pulisci('label'); }} obbligatorio errore={errori.label} autoFocus placeholder="es. Sostituzione" />
      <TextField
        label="Chiave" name="key" value={lk.key} onChange={(v) => { lk.setKey(v); pulisci('key'); }}
        obbligatorio={!modifica} disabilitato={modifica} errore={errori.key} mono
        suggerimento={modifica ? 'La chiave non è modificabile.' : 'Identificativo interno: minuscole, numeri e _.'}
      />
      <SelectField
        label="Comportamento sulla giacenza" name="tipo_movimento" value={comportamento}
        onChange={(v) => { setComportamento(v); pulisci('tipo_movimento'); }}
        opzioni={COMPORTAMENTI} obbligatorio errore={errori.tipo_movimento}
      />
      <InfoBox tono="warn">
        Attenzione: un nuovo tipo funziona solo attraverso uno dei tre comportamenti predefiniti
        (scarico, carico o nessuna variazione). Il comportamento non si può cambiare se esistono già interventi di questo tipo.
      </InfoBox>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <TextField label="Ordine" name="ordine" type="number" value={ordine} onChange={(v) => { setOrdine(v); pulisci('ordine'); }} errore={errori.ordine} />
      </div>
      <ToggleField label="Attivo" sublabel={attivo ? 'Selezionabile' : 'Non selezionabile'} checked={attivo} onChange={setAttivo} />
    </FormModal>
  );
};

const PillComportamento = ({ tipoMovimento }) => {
  if (tipoMovimento === 'sostituzione') return <StatusPill tono="error" label="Scarico (−)" />;
  if (tipoMovimento === 'rientrato') return <StatusPill tono="ok" label="Carico (+)" />;
  return <StatusPill tono="neutro" label="Nessuna variazione" />;
};

const COLONNE_TIPI = [
  { key: 'ordine', label: 'Ordine', width: 90, align: 'center', render: (r) => <span className="text-[12px] font-bold tabular-nums text-muted-foreground">{r.ordine}</span> },
  { key: 'label', label: 'Etichetta', width: 220, render: (r) => <span className="text-[13px] font-black uppercase tracking-tight text-foreground/80">{r.label}</span> },
  { key: 'key', label: 'Chiave', width: 180, render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground">{r.key}</span> },
  { key: 'tipo_movimento', label: 'Giacenza', width: 190, align: 'center', render: (r) => <PillComportamento tipoMovimento={r.tipo_movimento} /> },
  colonnaAttivo,
];

// ─── Unità di misura ─────────────────────────────────────────────────────────

const FormUnita = ({ record, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const lk = useLabelKey(record);
  const [decimali, setDecimali] = useState(record ? String(record.decimali) : '0');
  const [attivo, setAttivo] = useState(record?.attivo ?? true);
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();
  const sporco = useSporco({ label: lk.label, key: lk.key, decimali, attivo });

  const invia = async () => {
    const nuovi = validaLabelKey(lk.label, lk.key, modifica);
    if (vuoto(decimali)) nuovi.decimali = 'Obbligatorio';
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const payload = { label: lk.label.trim(), decimali: Number(decimali), attivo, ...(modifica ? {} : { key: lk.key }) };
    await salva(
      () => (modifica ? colataAggiorna('unita', record.id, payload) : colataCrea('unita', payload)),
      modifica ? 'Unità di misura aggiornata.' : 'Unità di misura creata.',
      onSaved,
    );
  };

  return (
    <FormModal titolo={modifica ? 'Modifica unità di misura' : 'Nuova unità di misura'} etichetta="Codifiche · Unità di misura" larghezza="md" sporco={sporco} salvando={salvando} onClose={onClose} onSubmit={invia}>
      <TextField label="Etichetta" name="label" value={lk.label} onChange={(v) => { lk.setLabel(v); pulisci('label'); }} obbligatorio errore={errori.label} autoFocus placeholder="es. M" />
      <TextField
        label="Chiave" name="key" value={lk.key} onChange={(v) => { lk.setKey(v); pulisci('key'); }}
        obbligatorio={!modifica} disabilitato={modifica} errore={errori.key} mono
        suggerimento={modifica ? 'La chiave non è modificabile.' : 'Identificativo interno: minuscole, numeri e _.'}
      />
      <SelectField
        label="Decimali ammessi" name="decimali" value={decimali} onChange={(v) => { setDecimali(v); pulisci('decimali'); }}
        opzioni={DECIMALI} obbligatorio errore={errori.decimali}
        suggerimento="Con 0 decimali la quantità si regola di 1 in 1; con più decimali è ammesso l'inserimento manuale."
      />
      <ToggleField label="Attiva" sublabel={attivo ? 'Selezionabile' : 'Non selezionabile'} checked={attivo} onChange={setAttivo} />
    </FormModal>
  );
};

const COLONNE_UNITA = [
  { key: 'label', label: 'Etichetta', width: 200, render: (r) => <span className="text-[13px] font-black uppercase tracking-tight text-foreground/80">{r.label}</span> },
  { key: 'key', label: 'Chiave', width: 180, render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground">{r.key}</span> },
  { key: 'decimali', label: 'Decimali', width: 120, align: 'center', render: (r) => <span className="text-[13px] font-black tabular-nums text-foreground/80">{r.decimali}</span> },
  colonnaAttivo,
];

// ─── Contenitore con sotto-tab ───────────────────────────────────────────────

const SOTTO_TAB = [
  { id: 'cause', label: 'Cause', icona: 'AlertTriangle' },
  { id: 'tipi', label: 'Tipi intervento', icona: 'Wrench' },
  { id: 'unita', label: 'Unità di misura', icona: 'Ruler' },
];

const TabCodifiche = () => {
  const [attivo, setAttivo] = useState('cause');

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-2">
        {SOTTO_TAB.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setAttivo(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm ${
              attivo === t.id
                ? 'bg-primary/10 border-primary/30 text-primary'
                : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={t.icona} size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {attivo === 'cause' && (
        <SezioneConfig
          key="cause"
          entita="cause"
          titolo="Cause"
          descrizione="Motivi di un intervento"
          nuovoLabel="Nuova causa"
          colonne={COLONNE_CAUSE}
          ordineIniziale={{ key: 'ordine', direction: 'asc' }}
          FormComponent={FormCausa}
          messaggioVuoto="Nessuna causa configurata"
        />
      )}
      {attivo === 'tipi' && (
        <SezioneConfig
          key="tipi"
          entita="tipi"
          titolo="Tipi di intervento"
          descrizione="Sostituzione, Rientrato, Ordinato…"
          nuovoLabel="Nuovo tipo"
          colonne={COLONNE_TIPI}
          ordineIniziale={{ key: 'ordine', direction: 'asc' }}
          FormComponent={FormTipo}
          messaggioVuoto="Nessun tipo di intervento configurato"
        />
      )}
      {attivo === 'unita' && (
        <SezioneConfig
          key="unita"
          entita="unita"
          titolo="Unità di misura"
          descrizione="Pezzi, metri, chilogrammi…"
          nuovoLabel="Nuova unità"
          colonne={COLONNE_UNITA}
          ordineIniziale={{ key: 'key', direction: 'asc' }}
          FormComponent={FormUnita}
          messaggioVuoto="Nessuna unità di misura configurata"
        />
      )}
    </div>
  );
};

export default TabCodifiche;
